import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import slide1 from '../images/slider1.jpeg';
import slide2 from '../images/slider2.jpeg';
import slide3 from '../images/slider3.jpeg';
import ContactModal from './ContactModal';

const Hero = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);

  const slides = [slide1, slide2, slide3];

  // Load Calendly script
  useEffect(() => {
    if (window.Calendly) {
      setCalendlyLoaded(true);
      return;
    }
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => setCalendlyLoaded(true);
    document.body.appendChild(script);

    return () => {
      if (document.head.contains(link)) document.head.removeChild(link);
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: 'https://calendly.com/dev-lakche/30min' });
    }
  };

  // Auto-slide effect
  useEffect(() => {
    const timer = setInterval(() => setCurrentSlide((prev) => (prev + 1) % slides.length), 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  // Updated Stats Array with Translation Keys
  const stats = [
    { 
      topLabel: t('stats.years_exp'), 
      number: "10+", 
      bottomLabel: t('stats.solutions') 
    },
    { 
      topLabel: t('stats.reliability'), 
      number: "99%", 
      bottomLabel: t('stats.satisfaction') 
    },
    { 
      topLabel: t('stats.scalability'), 
      number: "3x", 
      bottomLabel: t('stats.growth') 
    },
    { 
      topLabel: t('stats.trust'), 
      number: "∞", 
      bottomLabel: t('stats.possibilities') 
    },
  ];

  return (
    <div className="relative">
      {/* Hero Content */}
      <section id="hero" className="relative min-h-screen flex flex-col justify-center overflow-hidden">
        {/* Background Slideshow */}
        <div className="absolute inset-0 z-0">
          {slides.map((slide, index) => (
            <div 
              key={index} 
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img 
                src={slide} 
                alt={`Slide ${index + 1}`} 
                className="w-full h-full object-cover" 
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
            </div>
          ))}
        </div>

        {/* Hero Text Content */}
        <div className="relative z-10 container mx-auto px-4 md:px-12 pt-32 pb-44">
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-6">
              <span className="block">{t('hero.title1')}</span>
              <span className="block bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
                {t('hero.title2')}
              </span>
              <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl mt-2">
                {t('hero.title3')}
              </span>
            </h1>

            <p className="text-base sm:text-lg lg:text-xl text-gray-200 font-medium max-w-2xl leading-relaxed mb-10">
              {t('hero.description')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto pb-12">
              <button
                type="button"
                onClick={openCalendly}
                disabled={!calendlyLoaded}
                className={`group relative inline-flex items-center justify-center px-8 py-4 text-base sm:text-lg font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                  !calendlyLoaded ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <span className="relative flex items-center gap-2">
                  {t('hero.cta_products')}
                  <svg 
                    className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor" 
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>
              
              <button 
                onClick={() => setIsContactModalOpen(true)}
                className="group inline-flex items-center justify-center px-8 py-4 text-base sm:text-lg font-bold text-blue-700 bg-white border-2 border-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg"
              >
                {t('hero.cta_quote')}
                <svg 
                  className="w-5 h-5 ml-2 transform group-hover:rotate-12 transition-transform" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'w-8 bg-white' 
                  : 'w-2 bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <div className="relative z-40 -mt-24 md:-mt-36 px-4 md:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="shadow-2xl overflow-hidden grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 rounded-xl">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className={`flex flex-col items-center justify-center py-16 md:py-28 px-6 text-center transition-all duration-300 hover:scale-105 ${
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}
              >
                {/* Top Label */}
                <div className="mb-6 text-2xl md:text-xl font-semibold text-gray-500 text-center">
                  {stat.topLabel}
                </div>

                {/* Number */}
                <div className="text-4xl md:text-6xl font-light text-gray-800 mb-2">
                  {stat.number}
                </div>

                {/* Bottom Label */}
                <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-bold px-2">
                  {stat.bottomLabel}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Spacing */}
      <div className="h-20 bg-white"></div>

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </div>
  );
};

export default Hero;